import { memo, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { rateBins } from '../components/overlay/tracksModel';
import { flow, Label, Small, UnderPanel, UnderHead } from './primitives';

/** Strips under the timeline: animals counted per second (results) and a filmstrip of the clip (other steps). */

const Bars = styled.div`
	position: relative;
	flex: 1;
	min-height: 40px;
	display: flex;
	align-items: flex-end;
	gap: 2px;
	border-bottom: 1px solid ${flow.rule};
	button {
		flex: 1;
		min-width: 0;
		padding: 0;
		border: 0;
		border-radius: 2px 2px 0 0;
		background: rgba(32, 172, 107, 0.32);
		cursor: pointer;
	}
	button.past {
		background: ${({ theme }) => theme.colors.green};
	}
	button:hover,
	button:focus-visible {
		background: ${({ theme }) => theme.colors.lightGreen};
		outline: none;
	}
`;

// the strip's y-axis: whole numbers at the top, the middle and 0, with faint guide lines across the bars
const Chart = styled.div`
	display: flex;
	flex: 1;
	min-height: 0;
	gap: ${({ theme }) => theme.token.marginXS}px;
`;

const Axis = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: flex-end;
	min-width: ${({ theme }) => theme.token.controlHeightXS}px;
	font-size: ${({ theme }) => theme.token.fontSizeXS}px;
	line-height: 1;
	color: ${({ theme }) => theme.token.colorTextTertiary};
	font-variant-numeric: tabular-nums;
`;

const Guide = styled.span`
	position: absolute;
	left: 0;
	right: 0;
	border-top: ${({ theme }) => `${theme.token.lineWidth}px dashed ${theme.token.colorBorder}`};
	pointer-events: none;
`;

// the smallest round number at or above the highest bar: 1, 2, 4, 5, 10, 20, 40, 50, 100 …
const axisTop = peak => {
	if (peak <= 2) return Math.max(1, Math.ceil(peak));
	const p = 10 ** Math.floor(Math.log10(peak));
	return [1, 2, 4, 5, 10].map(m => m * p).find(v => v >= peak);
};

const Head = styled.span`
	position: absolute;
	top: 0;
	bottom: -4px;
	width: 2px;
	background: #fff;
	pointer-events: none;
`;

/**
 * @param series cumulative count per frame (same length as the clip in frames)
 * @param fps frames per second of the series
 * @param frame current frame
 * @param onSeek (seconds) => void
 */
/** cumulative: the series is a running total (bars = how many were added); otherwise bars = the highest value
 * (the video page shows how many are in view: the most at once in each stretch). */
export const RateStrip = memo(
	({ series, fps, frame, onSeek, title = 'Counted', cumulative = true, compact = false }) => {
		const { bins, binSec } = useMemo(
			() => rateBins(series, fps, cumulative),
			[series, fps, cumulative]
		);
		if (!bins.length) return null;
		const peak = axisTop(Math.max(1, ...bins));
		const ticks = peak % 2 === 0 ? [peak, peak / 2, 0] : [peak, 0];
		const now = frame / fps;
		const at = s =>
			`${Math.floor(s / 60)}:${(s % 60).toFixed(binSec < 1 ? 1 : 0).padStart(binSec < 1 ? 4 : 2, '0')}`;
		// compact: a thin strip of bars only, under a bigger video
		return (
			<UnderPanel
				style={compact ? { minHeight: 0, padding: '0.375rem 0.75rem' } : undefined}
				aria-label={compact ? title : undefined}>
				{!compact && (
					<UnderHead>
						<Label>{title}</Label>
						<Small>Click a bar to jump there</Small>
					</UnderHead>
				)}
				<Chart>
					{!compact && (
						<Axis aria-hidden>
							{ticks.map(v => (
								<span key={v}>{v}</span>
							))}
						</Axis>
					)}
					<Bars>
						{ticks.slice(0, -1).map(v => (
							<Guide key={v} style={{ bottom: `${(v / peak) * 100}%` }} />
						))}
						{bins.map((v, k) => {
							const t = k * binSec;
							return (
								<button
									key={k}
									type="button"
									className={t < now ? 'past' : ''}
									style={{ height: `${Math.max(3, (v / peak) * 100)}%` }}
									onClick={() => onSeek?.(t)}
									aria-label={`${v} at ${at(t)}`}
									title={`${v} at ${at(t)} · click to go there`}
								/>
							);
						})}
						<Head
							style={{
								left: `${Math.min(100, (now / (bins.length * binSec)) * 100)}%`,
							}}
						/>
					</Bars>
				</Chart>
			</UnderPanel>
		);
	}
);
RateStrip.displayName = 'RateStrip';

const Frames = styled.div`
	position: relative;
	flex: 1;
	min-height: 40px;
	display: grid;
	grid-template-columns: repeat(${({ $n }) => $n}, 1fr);
	gap: 4px;
`;

const Slot = styled.div`
	position: relative;
	min-height: 0;
	overflow: hidden;
	border-radius: 6px;
	background: #1f231f;
	canvas {
		display: block;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
`;

// the animals found so far on a thumbnail; the viewBox is the video frame, cropped like the canvas (cover)
const Marks = styled.svg`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	path {
		fill: ${({ theme }) => theme.colors.green};
		fill-opacity: 0.85;
	}
`;

/** Thumbnails in a FilmStrip. */
export const FILM_STRIP_N = 10;

/** The video time (s) each thumbnail shows: the middle of its share of the video. */
export const stripTimes = (n, duration) =>
	Array.from({ length: n }, (_, k) => ((k + 0.5) / n) * duration);

// a mark's radius is this share of the frame width: about two screen pixels on a thumbnail a tenth of the stage
// wide, whatever the video's resolution (a 4K frame divided by 150 was under a pixel and invisible)
const MARK_DIVISOR = 45;

// one path of circles per thumbnail: hundreds of animals stay one element
const dotsPath = (points, r) =>
	points
		.map(([x, y]) => `M${x - r} ${y}a${r} ${r} 0 1 0 ${2 * r} 0a${r} ${r} 0 1 0 ${-2 * r} 0`)
		.join('');

/**
 * Thumbnails drawn straight from the video into canvases (drawing a cross-origin video is allowed; only reading the
 * pixels back would not be). Seeks a hidden video through `n` evenly spaced times.
 */
// stills: [{ t, bitmap }] from a video the browser cannot play (.ts); the strip shows the ones nearest each slot.
// marks: per thumbnail, the animals found there so far ([[x, y], ...] in the pixels of a frameSize video frame).
// onDuration: the video's length (s) once known; the project's own duration_seconds is often empty.
export const FilmStrip = memo(
	({
		src,
		stills,
		n = FILM_STRIP_N,
		title = 'Preview',
		right,
		marks,
		frameSize,
		onDuration,
		children,
	}) => {
		const canvases = useRef([]);
		const durationRef = useRef(onDuration);
		durationRef.current = onDuration;
		useEffect(() => {
			if (!stills?.length) return;
			const end = stills[stills.length - 1].t || 1;
			for (let k = 0; k < n; k += 1) {
				const c = canvases.current[k];
				const at = ((k + 0.5) / n) * end;
				const f = stills.reduce((b, x) =>
					Math.abs(x.t - at) < Math.abs(b.t - at) ? x : b
				);
				if (!c || !f.bitmap) continue;
				c.width = 240;
				c.height = Math.round((240 * f.bitmap.height) / f.bitmap.width);
				c.getContext('2d').drawImage(f.bitmap, 0, 0, c.width, c.height);
			}
		}, [stills, n]);
		useEffect(() => {
			if (!src || stills) return undefined;
			let cancelled = false;
			const v = document.createElement('video');
			v.muted = true;
			v.preload = 'auto';
			v.playsInline = true;
			v.src = src;
			let i = 0;
			const times = [];
			const draw = () => {
				const c = canvases.current[i];
				if (c && v.videoWidth) {
					c.width = 240;
					c.height = Math.round((240 * v.videoHeight) / v.videoWidth);
					c.getContext('2d').drawImage(v, 0, 0, c.width, c.height);
				}
				i += 1;
				if (!cancelled && i < times.length) v.currentTime = times[i];
			};
			const onMeta = () => {
				const d = v.duration || 0;
				if (d) durationRef.current?.(d);
				times.push(...stripTimes(n, d));
				v.currentTime = times[0];
			};
			v.addEventListener('loadedmetadata', onMeta);
			v.addEventListener('seeked', draw);
			return () => {
				cancelled = true;
				v.removeEventListener('loadedmetadata', onMeta);
				v.removeEventListener('seeked', draw);
				v.removeAttribute('src');
				v.load();
			};
		}, [src, n, stills]);
		return (
			<UnderPanel>
				<UnderHead>
					<Label>{title}</Label>
					{right && <Small>{right}</Small>}
				</UnderHead>
				<Frames $n={n}>
					{Array.from({ length: n }, (_, k) => (
						<Slot key={k}>
							<canvas
								ref={el => {
									canvases.current[k] = el;
								}}
								aria-hidden="true"
							/>
							{frameSize && marks?.[k]?.length > 0 && (
								<Marks
									viewBox={`0 0 ${frameSize.width} ${frameSize.height}`}
									preserveAspectRatio="xMidYMid slice"
									aria-hidden="true">
									<path d={dotsPath(marks[k], frameSize.width / MARK_DIVISOR)} />
								</Marks>
							)}
						</Slot>
					))}
					{children}
				</Frames>
			</UnderPanel>
		);
	}
);
FilmStrip.displayName = 'FilmStrip';
